import { useState, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Sparkles, ArrowRight, ArrowLeft, Check, AlertCircle, Loader2, FileSpreadsheet } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * ImportMappingWizard — Modal reusable de 3 pasos:
 *   1. Subir Excel/CSV + (opcional) nombre de proveedor
 *   2. Ver mapeo sugerido por IA, ajustar dropdowns, guardar mapeo por proveedor
 *   3. Preview de 5 filas remapeadas, botón "Continuar" → onDone({headers, mapping, preview, filename, remappedRows})
 *
 * Props:
 *   open, onOpenChange
 *   title (default: "Importar Excel")
 *   requiredFields: array de campos canónicos requeridos (default: ['sku','nombre'])
 *   onDone: callback con {headers, mapping, rows (todas las filas remapeadas), filename, proveedor}
 */
export default function ImportMappingWizard({
  open,
  onOpenChange,
  title = "Importar Excel del proveedor",
  requiredFields = ["sku", "nombre"],
  onDone,
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [proveedor, setProveedor] = useState("");
  const [loading, setLoading] = useState(false);
  const [detection, setDetection] = useState(null); // {headers, preview, mapping_sugerido, campos_canonicos}
  const [mapping, setMapping] = useState({}); // {campo: col_index}
  const [savingMapping, setSavingMapping] = useState(false);
  const [saveForFuture, setSaveForFuture] = useState(true);
  const fileInputRef = useRef();

  const reset = () => {
    setStep(1);
    setFile(null);
    setProveedor("");
    setDetection(null);
    setMapping({});
    setSaveForFuture(true);
  };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const detectarColumnas = async () => {
    if (!file) {
      toast.error("Selecciona un archivo primero");
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (proveedor.trim()) form.append("proveedor", proveedor.trim());
      const { data } = await api.post("/importar/detectar-columnas", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDetection(data);
      // Inicializar mapping desde sugerencia (o mapping_previo si existe)
      const source = data.mapping_previo || data.mapping_sugerido || {};
      const init = {};
      Object.entries(source).forEach(([campo, info]) => {
        init[campo] = typeof info === "number" ? info : info.col_index;
      });
      setMapping(init);
      setStep(2);
      const modo = data.modo === "ai" ? "IA (Claude)" : "coincidencia fuzzy";
      toast.success(`Detecté ${Object.keys(init).length} columnas con ${modo}. Revisa antes de continuar.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo analizar el archivo");
    } finally {
      setLoading(false);
    }
  };

  const validateMapping = () => {
    for (const req of requiredFields) {
      if (mapping[req] === undefined || mapping[req] === null || mapping[req] === "") {
        return `Falta mapear el campo obligatorio: ${req}`;
      }
    }
    // No repetir col_index
    const seen = new Set();
    for (const [campo, idx] of Object.entries(mapping)) {
      if (idx === undefined || idx === null || idx === "") continue;
      if (seen.has(idx)) {
        return `La columna "${detection.headers[idx]}" está asignada a dos campos. Cada columna debe ir a uno solo.`;
      }
      seen.add(idx);
    }
    return null;
  };

  const finalizar = async () => {
    const err = validateMapping();
    if (err) {
      toast.error(err);
      return;
    }
    // Guardar mapeo por proveedor (best-effort)
    if (saveForFuture && proveedor.trim()) {
      setSavingMapping(true);
      try {
        const cleanMap = {};
        Object.entries(mapping).forEach(([c, i]) => {
          if (i !== undefined && i !== null && i !== "") cleanMap[c] = Number(i);
        });
        await api.post("/importar/mapeos", { proveedor: proveedor.trim(), mapping: cleanMap });
      } catch (_) {
        // best-effort: no bloqueamos el flujo
      } finally {
        setSavingMapping(false);
      }
    }
    // Callback con las primeras filas remapeadas (el caller puede pedir el archivo completo si quiere)
    const remapPreview = detection.preview.map((row) => {
      const out = {};
      Object.entries(mapping).forEach(([campo, idx]) => {
        if (idx !== undefined && idx !== null && idx !== "") {
          out[campo] = row[Number(idx)] ?? "";
        }
      });
      return out;
    });
    onDone?.({
      headers: detection.headers,
      mapping,
      previewMapped: remapPreview,
      previewRaw: detection.preview,
      filename: detection.filename,
      proveedor: proveedor.trim() || null,
      file, // el caller puede volver a subirlo si necesita todas las filas
    });
    handleOpenChange(false);
  };

  const updateMapping = (campo, colIdx) => {
    setMapping((m) => ({ ...m, [campo]: colIdx === "" ? undefined : Number(colIdx) }));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="import-mapping-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-4 text-xs">
          {[1, 2, 3].map((n, i) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold ${
                  step >= n ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}
              >
                {step > n ? <Check className="h-4 w-4" /> : n}
              </div>
              <span className={step >= n ? "font-semibold" : "text-muted-foreground"}>
                {n === 1 ? "Cargar" : n === 2 ? "Mapear columnas" : "Confirmar"}
              </span>
              {i < 2 && <div className={`h-px flex-1 ${step > n ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="mb-1">Proveedor (opcional)</Label>
              <Input
                placeholder="ej: Anclad, Yiwu Textiles..."
                value={proveedor}
                onChange={(e) => setProveedor(e.target.value)}
                data-testid="mapping-proveedor-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Si escribes el proveedor, guardaré el mapeo para reusarlo la próxima vez que te manden un Excel de ellos.
              </p>
            </div>
            <div>
              <Label className="mb-1">Archivo Excel o CSV</Label>
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) setFile(f);
                }}
                onDragOver={(e) => e.preventDefault()}
                data-testid="mapping-file-drop"
              >
                <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                {file ? (
                  <div>
                    <p className="font-semibold">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB · Click para cambiar</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm">Arrastra tu Excel aquí o click para seleccionar</p>
                    <p className="text-xs text-muted-foreground mt-1">.xlsx, .xls, .csv (máx 10 MB)</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>
            <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 text-sm">
              <p className="flex items-center gap-2 font-semibold text-primary">
                <Sparkles className="h-4 w-4" /> Detección con IA
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                La IA (Claude) lee las columnas del proveedor —aunque estén en inglés o chino— y sugiere a qué campo del sistema
                corresponde cada una. Tú confirmas antes de importar.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancelar</Button>
              <Button
                onClick={detectarColumnas}
                disabled={!file || loading}
                data-testid="mapping-analyze-btn"
              >
                {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analizando...</>) : (<>Analizar con IA <ArrowRight className="h-4 w-4 ml-2" /></>)}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2: Mapping */}
        {step === 2 && detection && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="rounded-lg bg-muted/50 p-3 text-xs">
              <strong>{detection.filename}</strong> · {detection.headers.length} columnas detectadas ·{" "}
              modo: <span className="font-semibold text-primary">{detection.modo === "ai" ? "IA" : "coincidencia"}</span>
              {detection.mapping_previo && <span className="ml-2 text-emerald-600">✓ Mapeo previo cargado</span>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(detection.campos_canonicos).map(([campo, descripcion]) => {
                const isRequired = requiredFields.includes(campo);
                const sug = detection.mapping_sugerido?.[campo];
                const currentIdx = mapping[campo];
                return (
                  <div
                    key={campo}
                    className={`rounded-lg border p-3 ${
                      isRequired && (currentIdx === undefined || currentIdx === null || currentIdx === "")
                        ? "border-rose-300 bg-rose-50/50"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs font-semibold">
                        {campo}
                        {isRequired && <span className="text-rose-500 ml-1">*</span>}
                      </Label>
                      {sug && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary" title={`IA sugirió con ${sug.score}% de confianza`}>
                          {sug.source === "ai" ? "🤖 IA" : "≈ auto"} {sug.score}%
                        </span>
                      )}
                    </div>
                    <select
                      className="w-full text-sm rounded-md border border-input bg-background px-2 py-1.5"
                      value={currentIdx ?? ""}
                      onChange={(e) => updateMapping(campo, e.target.value)}
                      data-testid={`mapping-select-${campo}`}
                    >
                      <option value="">— No mapear —</option>
                      {detection.headers.map((h, i) => (
                        <option key={i} value={i}>{h || `(Columna ${i + 1})`}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-1">{descripcion}</p>
                  </div>
                );
              })}
            </div>

            <div className="pt-2 border-t">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={saveForFuture}
                  onChange={(e) => setSaveForFuture(e.target.checked)}
                  disabled={!proveedor.trim()}
                />
                <span>Guardar este mapeo para el próximo Excel de <strong>{proveedor.trim() || "(sin proveedor)"}</strong></span>
              </label>
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" /> Atrás</Button>
              <Button onClick={() => setStep(3)} data-testid="mapping-next-preview-btn">
                Ver vista previa <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Preview */}
        {step === 3 && detection && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              Así se verán las primeras {detection.preview.length} filas con el mapeo aplicado. Si algo se ve mal, vuelve atrás y corrígelo.
            </p>
            <div className="rounded-lg border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    {Object.keys(mapping)
                      .filter((c) => mapping[c] !== undefined && mapping[c] !== null && mapping[c] !== "")
                      .map((c) => (
                        <th key={c} className="p-2 text-left">
                          <span className="font-semibold">{c}</span>
                          <br />
                          <span className="text-[10px] text-muted-foreground font-normal">{detection.headers[mapping[c]]}</span>
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {detection.preview.map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 text-muted-foreground">{i + 1}</td>
                      {Object.keys(mapping)
                        .filter((c) => mapping[c] !== undefined && mapping[c] !== null && mapping[c] !== "")
                        .map((c) => (
                          <td key={c} className="p-2">{row[Number(mapping[c])] ?? "—"}</td>
                        ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(() => {
              const err = validateMapping();
              return err ? (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm flex items-center gap-2 text-rose-700">
                  <AlertCircle className="h-4 w-4" /> {err}
                </div>
              ) : null;
            })()}
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-2" /> Atrás</Button>
              <Button onClick={finalizar} disabled={!!validateMapping() || savingMapping} data-testid="mapping-finalize-btn">
                {savingMapping ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>) : (<><Check className="h-4 w-4 mr-2" /> Confirmar y continuar</>)}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
